import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "@mui/material/Select";
import Input from "@mui/material/Input";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from '@mui/material/ListItemText';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import DateRangeFilter from '../../components/searchBar/DateRangeFilter';
import {
  DEFAULT_FCS_CCTRANSACTION_STATE,
} from "../../constants/constants";
import * as types from '../../constants/actionTypes';
import AddCircle from '@mui/icons-material/AddCircle';
import UserLevelFilter from "../../components/searchBar/UserLevelFilter";
import { makeStyles } from "@mui/styles";
import theme from '../../constants/theme';
import CCDropDownMenu from "../CCTransactionReport/CCDropDownMenu";
import {
  apiGetFcsCCTransactionChannelData,
  apiGetFcsCCTransactionTransTypeData,
  apiGetFcsCCTransactionStatusData,
  apiGetFcsCCTransactionSdVerifyData
} from "../../resource/index.js";
import Storage from "../../common/lib/storage";

const useStyles = makeStyles(() => ({
  formControl: {
    margin: theme.spacing(1),
    display: "flex",
    fontStyle: "italic",
  },
  selectControl: {
    width: 250,
  },
  selectedFilters: {
    display: "flex",
    flexWrap: "wrap",
  },
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
}));

const FcsCCTransactionFilter = () => {
  const classes = useStyles();
  const { filters, levelId, showAll, userAndLevelName, fcsSelectedCreditCardChannel, fcsSelectedCcTransactionType, fcsSelectedCreditCardStatus, fcsSelectedSourceDataVerifyStatus, fcsCcTransactionState } = useSelector((state) => state.report);
  const dispatch = useDispatch();
  const setFilters = (filters) => dispatch({ type: types.SET_FILTERS, filters });
  const setSelectedCcChannel = (val) => dispatch({ type: types.SET_FCS_SELECTED_CREDITCARD_CHANNEL, value: val });
  const setSelectedTransactionType = (val) => dispatch({ type: types.SET_FCS_SELECTED_CCTRANSACTION_TYPE, value: val });
  const setSelectedCcStatus = (val) => dispatch({ type: types.SET_FCS_SELECTED_CREDITCARD_STATUS, value: val });
  const setSelectedSourceDataVerify = (val) => dispatch({ type: types.SET_FCS_SELECTED_SOURCEDATAVERIFY_STATUS, value: val });
  const setFcsCCTransactionState = (val) => dispatch({ type: types.SET_FCS_CCTRANSACTION_STATE, value: val });
  const setUserAndLevelName = (val) => dispatch({ type: types.SET_USER_AND_LEVEL_NAME, value: val });
  const setLevelId = (val) => dispatch({ type: types.SET_LEVEL_ID, value: val });
  const setShowAll = (val) => dispatch({ type: types.SET_SHOW_ALL, value: val });

  const [ccStatusObject, setCcStatusObject] = useState({});
  const [ccStatusMenuList, setCcStatusMenuList] = useState(null);
  const [pathname, setPathname] = useState(window.location.pathname);

  const filterList = [
    {
      label: "Date Range",
      value: "dateRange",
      keys: ["fromDate", "toDate"],
      required: true,
    },
    {
      label: "User & Level",
      value: "userAndLeve",
      keys: ["userAndLeve"],
      required: true,
    },
    {
      label: "Credit Card Channels",
      value: "creditCardChannel",
      keys: ["creditCardChannel"],
      show: true,
    },
    {
      label: "Transaction Type",
      value: "transactionType",
      keys: ["transactionType"],
      show: true,
    },
    {
      label: "Credit Card Status",
      value: "ccStatus",
      keys: ["ccStatus"],
      show: true,
    },
    {
      label: "Source Data Verified",
      value: "sourceDataVerified",
      keys: ["sourceDataVerified"],
      show: true,
    },
  ];

  const [selectedFilters, setSelectedFilters] = useState(
    filterList.filter((r) => r.required || r.show).map((r) => r.value)
  );

  const toggleFilter = (val) => {
    setSelectedFilters(val);
    const filterKeys = filterList
      .filter((r) => val.includes(r.value))
      .flatMap((r) => r.keys);
    const newFilters = { ...fcsCcTransactionState};
    Object.keys(fcsCcTransactionState).forEach((r) => {
      if (!(filterKeys.includes(r) )) {
        newFilters[r] = DEFAULT_FCS_CCTRANSACTION_STATE[r];
      }
    });
    setFcsCCTransactionState(newFilters);
    setCcStatusMenuList(null);
  };

  const isVisible = (id) => {
    return selectedFilters.includes(id);
  };

  const selectedFilterCount = selectedFilters.length;

  useEffect(() => {
    const {
      fromDate,
      toDate,
      selectedCreditCardChannel,
      selectedTransactionType,
      selectedCcStatus,
      selectedSourceDataVerifyStatus,
    } = fcsCcTransactionState;

    setFilters({
      fromDate,
      toDate,
      selectedCreditCardChannel,
      selectedTransactionType,
      selectedCcStatus,
      selectedSourceDataVerifyStatus,
      levelId,
      showAll,
    });
  }, [fcsCcTransactionState, fcsSelectedCreditCardChannel, fcsSelectedCcTransactionType, fcsSelectedCreditCardStatus, fcsSelectedSourceDataVerifyStatus, levelId, showAll]);

  const setStatusMenu = (inputObject, selectedValue) => {
    if (selectedValue === undefined || inputObject[selectedValue] === undefined) {
      setCcStatusMenuList("");
    } else {
      setCcStatusMenuList(inputObject[selectedValue]);
    }
  };

  const fetchStatusData = (selectedValue) => {
    const regulator = Storage.getRegulator() ?? "";
    apiGetFcsCCTransactionStatusData(regulator)
      .then((res) => {
        if (res.data.status === "success") {
          setCcStatusObject(res.data.data.creditCardStatus);
          setStatusMenu(res.data.data.creditCardStatus, selectedValue)
        }
      })
      .catch((e) => {
        setCcStatusObject({});
      });
  };

  React.useEffect(() => {
    const handlePathnameChange = () => {
      setPathname(window.location.pathname);
    };
    handlePathnameChange();
    window.addEventListener('popstate', handlePathnameChange);
    return () => {
      window.removeEventListener('popstate', handlePathnameChange);
    };
  }, []);

  React.useEffect(() => {
    setFcsCCTransactionState(DEFAULT_FCS_CCTRANSACTION_STATE);
  }, [pathname]);

  return (
    <>
      <div className={classes.formControl}>
        <InputLabel id="filter-mutiple-checkbox-label">
          <AddCircle color="primary" />
        </InputLabel>
        <Select
          className={classes.selectControl}
          labelId="filter-mutiple-checkbox-label"
          id="filter-mutiple-checkbox"
          multiple
          value={selectedFilters}
          onChange={(e) => toggleFilter(e.target.value)}
          input={<Input />}
          renderValue={(selected) => `${selectedFilterCount} filters selected`}
        >
          {filterList.map((option) => (
            <MenuItem
              key={option.value}
              disabled={option.required}
              value={option.value}
            >
              <Checkbox
                color="primary"
                checked={selectedFilters.includes(option.value)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <DateRangeFilter
        selectedFromDate={fcsCcTransactionState.fromDate}
        setSelectedFromDate={(val) => setFcsCCTransactionState({ ...fcsCcTransactionState, fromDate: val })}
        selectedToDate={fcsCcTransactionState.toDate}
        setSelectedToDate={(val) => setFcsCCTransactionState({ ...fcsCcTransactionState, toDate: val })}
      />

      {isVisible("userAndLeve") && (
          <UserLevelFilter
            levelId={levelId}
            setLevelId={setLevelId}
            showAll={showAll}
            setShowAll={setShowAll}
            userAndLevelName={userAndLevelName}
            setUserAndLevelName={setUserAndLevelName}
          />
        )}

      {isVisible("creditCardChannel") && (
        <CCDropDownMenu
          label="Credit Card Channels"
          value={fcsCcTransactionState.selectedCreditCardChannel}
          defaultValue="All Channels"
          apiFunction={apiGetFcsCCTransactionChannelData}
          dataKey="creditCardChannel"
          onChange={(val) => {
            setFcsCCTransactionState({ ...fcsCcTransactionState, selectedCreditCardChannel: val})
            setSelectedCcChannel(val)
          }}
        />
      )}

      {isVisible("transactionType") && (
        <CCDropDownMenu
          label="Transaction Type"
          value={fcsCcTransactionState.selectedTransactionType}
          defaultValue="All Transaction Types"
          apiFunction={apiGetFcsCCTransactionTransTypeData}
          dataKey="creditCardTransType"
          onChange={(val) => {
            setFcsCCTransactionState({ ...fcsCcTransactionState, selectedTransactionType: val, selectedCcStatus: DEFAULT_FCS_CCTRANSACTION_STATE.selectedCcStatus})
            setSelectedTransactionType(val)
            if (Object.keys(ccStatusObject).length === 0) {
              fetchStatusData(val);
            } else {
              setStatusMenu(ccStatusObject, val);
            }
          }}
        />
      )}

      {isVisible("ccStatus") && (
        <CCDropDownMenu
          label="Credit Card Status"
          value={fcsCcTransactionState.selectedCcStatus}
          defaultValue="All Status"
          apiFunction={null}
          dataKey="creditCardStatus"
          onChange={(val) => {
            setFcsCCTransactionState({ ...fcsCcTransactionState, selectedCcStatus: val})
            setSelectedCcStatus(val)
          }}
          inputMenuList={ccStatusMenuList}
        />
      )}

      {isVisible("sourceDataVerified") && (
        <CCDropDownMenu
          label="Source Data Verified"
          value={fcsCcTransactionState.selectedSourceDataVerifyStatus}
          defaultValue="All Verify Status"
          apiFunction={apiGetFcsCCTransactionSdVerifyData}
          dataKey="creditCardSdVerify"
          onChange={(val) => {
            setFcsCCTransactionState({ ...fcsCcTransactionState, selectedSourceDataVerifyStatus: val})
            setSelectedSourceDataVerify(val)
          }}
        />
      )}
    </>
  );
};

export default FcsCCTransactionFilter;
